'use client';

import {
  Alert02Icon,
  InformationCircleIcon,
  Tick01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import React, { type ReactNode, useEffect, useRef, useState } from 'react';
import { toast as sonnerToast } from 'sonner';
import { capture } from '@/lib/analytics';
import { sanitizePII } from '@/lib/analytics/sanitize';
import { reportClientError } from '@/lib/observability/report-client-error';
import { cn } from '@/lib/utils';

type ToastType = 'success' | 'error' | 'info';

const iconsByType: Record<ToastType, ReactNode> = {
  success: (
    <HugeiconsIcon
      aria-hidden="true"
      icon={Tick01Icon}
      strokeWidth={2}
      className="text-positive"
    />
  ),
  error: (
    <HugeiconsIcon
      aria-hidden="true"
      icon={Alert02Icon}
      strokeWidth={2}
      className="text-destructive"
    />
  ),
  // Neutral/informational — not a success or a failure (e.g. "session
  // expired, please sign in"). Muted, non-alarming.
  info: (
    <HugeiconsIcon
      aria-hidden="true"
      icon={InformationCircleIcon}
      strokeWidth={2}
      className="text-muted-foreground"
    />
  ),
};

interface ToastOptions {
  description?: string;
  /**
   * The caught error behind an error toast. Pass it from every catch so the
   * failure reaches error tracking: `toast.error(msg, { error })`.
   */
  error?: unknown;
  /** One button, e.g. Undo; the toast stays a little longer and closes on use. */
  action?: { label: string; onClick: () => void };
}

interface ToastObjectProps {
  type: ToastType;
  description: string;
}

function showToast(type: ToastType, message: string, options?: ToastOptions) {
  const description = options?.description
    ? `${message}: ${options.description}`
    : message;

  // Track error toasts for analytics with PII sanitization
  if (type === 'error') {
    try {
      capture('error_displayed', {
        errorType: 'toast',
        message: sanitizePII(description),
      });
    } catch (error) {
      // Silently fail - analytics should never prevent toast display
      console.error('[Analytics] Failed to capture error toast:', error);
    }

    if (options?.error !== undefined) {
      reportClientError(options.error, {
        source: 'toast',
        properties: { toast_description: sanitizePII(description) },
      });
    }
  }

  return sonnerToast.custom(
    (id) => (
      <Toast
        id={id}
        type={type}
        description={description}
        action={options?.action}
      />
    ),
    { duration: options?.action ? 8000 : 4000 },
  );
}

/**
 * Toast wrapper that provides PII sanitization and analytics tracking.
 * Use this instead of importing directly from 'sonner'.
 *
 * Supports two APIs:
 * 1. Object API (legacy): toast({ type: 'error', description: 'message' })
 * 2. Method API (preferred): toast.error('message') or toast.success('message')
 *
 * @example
 * toast.error('Something went wrong')
 * toast.error('Failed to save', { description: 'Network error' })
 * toast.error('Failed to save', { error }) // inside a catch: also reports it
 * toast.success('Saved successfully')
 * toast('Just a message') // defaults to success
 * toast({ type: 'error', description: 'Legacy API' }) // legacy object API
 */
export const toast = Object.assign(
  // Default function: supports both string and object API
  (messageOrProps: string | ToastObjectProps, options?: ToastOptions) => {
    if (typeof messageOrProps === 'object') {
      // Legacy object API: toast({ type: 'error', description: 'message' })
      return showToast(messageOrProps.type, messageOrProps.description);
    }
    // String API: toast('message')
    return showToast('success', messageOrProps, options);
  },
  {
    error: (message: string, options?: ToastOptions) =>
      showToast('error', message, options),
    success: (message: string, options?: ToastOptions) =>
      showToast('success', message, options),
    info: (message: string, options?: ToastOptions) =>
      showToast('info', message, options),
  },
);

function Toast(props: ToastProps) {
  const { id, type, description, action } = props;

  const descriptionRef = useRef<HTMLDivElement>(null);
  const [multiLine, setMultiLine] = useState(false);

  useEffect(() => {
    const el = descriptionRef.current;
    if (!el) return;

    const update = () => {
      const lineHeight = Number.parseFloat(getComputedStyle(el).lineHeight);
      const lines = Math.round(el.scrollHeight / lineHeight);
      setMultiLine(lines > 1);
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);

    return () => ro.disconnect();
  }, [description]);

  return (
    <div className="flex w-full toast-mobile:w-[356px] justify-center">
      <div
        data-testid="toast"
        key={id}
        className={cn(
          'bg-background p-4 rounded-lg w-full toast-mobile:w-fit flex flex-row gap-3 border border-border shadow-md',
          multiLine ? 'items-start' : 'items-center',
        )}
      >
        <div
          data-type={type}
          className={cn('', {
            'pt-1': multiLine,
          })}
        >
          {iconsByType[type]}
        </div>
        <div
          ref={descriptionRef}
          className="text-foreground text-sm text-center"
        >
          {description}
        </div>
        {action ? (
          <button
            type="button"
            className="shrink-0 rounded-md px-2 py-1 font-medium text-foreground text-sm underline-offset-2 hover:underline"
            onClick={() => {
              action.onClick();
              sonnerToast.dismiss(id);
            }}
          >
            {action.label}
          </button>
        ) : null}
      </div>
    </div>
  );
}

interface ToastProps {
  id: string | number;
  type: ToastType;
  description: string;
  action?: ToastOptions['action'];
}
